"use client"
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  FlexColumn,
  FlexEnd,
  FormControl,
  formatAddress,
  Input,
  Media,
  Popover,
  Separator,
  Spinner,
  TextLabel,
} from "@opensea/ui-kit"
import { useState } from "react"
import { useFormContext, useFormState, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import { useAddressInputField } from "../../../../../hooks/useAddressInput"
import type { ListingFlowFormValues } from "../../types"

export const ReserveButton = () => {
  const t = useTranslations("wallet.ReserveButton")
  const [isOpen, setIsOpen] = useState(false)
  const { control, setValue, register } =
    useFormContext<ListingFlowFormValues>()
  const { errors } = useFormState({ control })
  const reservedBuyer = useWatch({ control, name: "reservedBuyer" })

  useAddressInputField({
    field: "reservedBuyer",
    setValue,
    identifier: reservedBuyer.identifier,
  })

  const error = reservedBuyer.isResolving
    ? undefined
    : (errors.reservedBuyer?.identifier?.message ??
      errors.reservedBuyer?.resolvedAddress?.message)

  const trigger = (
    <Button
      className={error ? "!border-error-1" : ""}
      size="sm"
      variant="secondary"
    >
      {t("reserve")}
    </Button>
  )

  const addressInput = (
    <FlexColumn className="gap-1">
      <FormControl error={error} hideLabel label={t("reserveForBuyer")}>
        <Input
          autoFocus
          endEnhancer={
            reservedBuyer.isResolving ? (
              <Spinner color="text-secondary" size="2xs" />
            ) : undefined
          }
          placeholder={t("enterRecipientAddress")}
          size="sm"
          {...register("reservedBuyer.identifier")}
        />
      </FormControl>

      {reservedBuyer.resolvedAddress && (
        <FlexEnd>
          <TextLabel size="xs">
            {formatAddress(reservedBuyer.resolvedAddress)}
          </TextLabel>
        </FlexEnd>
      )}
    </FlexColumn>
  )

  return (
    <>
      <Media lessThan="md">
        <BottomSheet
          content={
            <>
              <BottomSheetHeader>
                <BottomSheetHeaderTitle>{t("reserve")}</BottomSheetHeaderTitle>
              </BottomSheetHeader>
              <BottomSheetBody>
                <FlexColumn className="gap-3">
                  <FlexColumn className="grow gap-3">
                    <TextLabel color="text-secondary" size="2xs">
                      {t("reserveForBuyer")}
                    </TextLabel>
                    {addressInput}
                  </FlexColumn>
                  <Separator
                    className="h-auto flex-none"
                    orientation="vertical"
                  />
                </FlexColumn>
              </BottomSheetBody>
              <BottomSheetFooter>
                <Button onClick={() => setIsOpen(false)} size="sm">
                  {t("confirm")}
                </Button>
              </BottomSheetFooter>
            </>
          }
          nested
          onOpenChange={setIsOpen}
          open={isOpen}
        >
          {trigger}
        </BottomSheet>
      </Media>
      <Media greaterThanOrEqual="md">
        <Popover
          className="w-[400px] bg-bg-primary"
          content={
            <FlexColumn className="gap-3">
              <TextLabel color="text-secondary" size="2xs">
                {t("reserveForBuyer")}
              </TextLabel>

              {addressInput}
            </FlexColumn>
          }
          // Default styles dont work because portal is false. When portal is true, for some reason the tooltip closes on click as well.
          onOpenChange={setIsOpen}
          open={isOpen}
          portal={false}
        >
          {trigger}
        </Popover>
      </Media>
    </>
  )
}
